import { createContext, useContext, useState, ReactNode } from 'react'

interface PageHeaderAction {
  id: string
  component: ReactNode
}

interface PageHeaderConfig {
  title: string
  description?: string
  actions: PageHeaderAction[]
}

interface PageHeaderContextType {
  headerConfig: PageHeaderConfig
  setHeaderConfig: (config: PageHeaderConfig) => void
  addAction: (action: PageHeaderAction) => void
  removeAction: (actionId: string) => void
  clearActions: () => void
}

const PageHeaderContext = createContext<PageHeaderContextType | undefined>(undefined)

interface PageHeaderProviderProps {
  children: ReactNode
}

export function PageHeaderProvider({ children }: PageHeaderProviderProps) {
  const [headerConfig, setHeaderConfigState] = useState<PageHeaderConfig>({
    title: '',
    description: '',
    actions: []
  })

  const setHeaderConfig = (config: PageHeaderConfig) => {
    setHeaderConfigState(config)
  }

  const addAction = (action: PageHeaderAction) => {
    setHeaderConfigState(prev => ({
      ...prev,
      actions: [...prev.actions.filter(a => a.id !== action.id), action]
    }))
  }

  const removeAction = (actionId: string) => {
    setHeaderConfigState(prev => ({
      ...prev,
      actions: prev.actions.filter(a => a.id !== actionId)
    }))
  }

  const clearActions = () => {
    setHeaderConfigState(prev => ({
      ...prev,
      actions: []
    }))
  }

  return (
    <PageHeaderContext.Provider value={{
      headerConfig,
      setHeaderConfig,
      addAction,
      removeAction,
      clearActions
    }}>
      {children}
    </PageHeaderContext.Provider>
  )
}

export function usePageHeader() {
  const context = useContext(PageHeaderContext)
  if (context === undefined) {
    throw new Error('usePageHeader must be used within a PageHeaderProvider')
  }
  return context
}